import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingState, useToast } from '@practium/ui';
import { mockCreditsData } from '@practium/features/flora-ai';
import { useGetPlanSummary } from '@practium/features/api';
import { useAuth } from '@practium/features/auth';
import { UserRole } from '@practium/shared';
import { CurrentPlanCard } from '../components/CurrentPlanCard/CurrentPlanCard';
import { CycleUsageCard } from '../components/CycleUsageCard/CycleUsageCard';
import { BuyTokensCard } from '../components/BuyTokensCard/BuyTokensCard';
import { SwitchPlanCard } from '../components/SwitchPlanCard/SwitchPlanCard';
import { InvoicesCard } from '../components/InvoicesCard/InvoicesCard';
import { CancelPlanCard } from '../components/CancelPlanCard/CancelPlanCard';
import { NextChargeCard } from '../components/NextChargeCard/NextChargeCard';
import { PaymentMethodCard } from '../components/PaymentMethodCard/PaymentMethodCard';
import { NeedMoreCard } from '../components/NeedMoreCard/NeedMoreCard';
import {
	formatCents,
	formatCycleRange,
	formatDate,
	formatDocument,
	formatMonthYear,
	getBillingCopy,
	getDocumentTypeLabel,
	getPaymentMethodLabel,
	getPaymentMethodSuffix,
} from '../utils/formatMyPlan';
import styles from './MyPlanPage.module.css';

export const MyPlanPage = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { user } = useAuth();
	const { data, isLoading, isError } = useGetPlanSummary();
	const isAccountScopedProps =
		user?.role === UserRole.Consultant || user?.role === UserRole.Company;

	useEffect(() => {
		if (isError) {
			toast.error('Não foi possível carregar os dados do plano. Tente novamente.');
		}
	}, [isError]);

	const priceLabel = useMemo(() => {
		if (!data?.plan) return '—';
		const { per, charge } = getBillingCopy(data.plan.interval, data.plan.intervalCount);
		const methodSuffix = getPaymentMethodSuffix(data.paymentMethod);
		const parts = [formatCents(data.plan.price), per, `- ${charge}`];
		if (methodSuffix) parts.push(methodSuffix);
		return parts.filter(Boolean).join(' ');
	}, [data]);

	if (isLoading) {
		return <LoadingState />;
	}

	if (isError || !data) {
		return (
			<div className={styles.page}>
				<p className={styles.error}>Não foi possível carregar os dados do plano.</p>
			</div>
		);
	}

	const isActive = data.displayStatus === 'active' || data.displayStatus === 'wait_cancel';
	const planName = data.plan?.name ?? 'Sem plano';
	const extraTokensAmount = 0;
	const chargeAmount = data.nextCharge?.amount ?? 0;
	const totalAmount = chargeAmount + extraTokensAmount;

	return (
		<div className={styles.page}>
			<div className={styles.layout}>
				<div className={styles.main}>
					<CurrentPlanCard
						planName={planName}
						priceLabel={priceLabel}
						isActive={isActive}
						cycleLabel={formatCycleRange(data.cycleStart, data.expiresIn)}
						renewsLabel={data.nextCharge ? formatDate(data.nextCharge.date) : 'Sem renovação'}
						sinceLabel={formatMonthYear(data.signedAt)}
						onUpgrade={
							data.canUpgrade ? () => navigate('/checkout/plano?upgrade=1') : undefined
						}
					/>
					<CycleUsageCard
						subtitle={
							data.expiresIn
								? `Os limites reiniciam em ${formatDate(data.expiresIn)}`
								: 'Limites do ciclo atual'
						}
						items={[
							{
								label: 'Consumo do ciclo',
								used: mockCreditsData.used,
								max: mockCreditsData.total,
								hint: 'Tokens da Flora',
								variant: 'token',
							},
							{
								label: 'Propriedades cadastradas',
								used: data.usage.props.used,
								max: data.usage.props.max,
								hint: isAccountScopedProps ? 'Nesta conta' : undefined,
							},
							{
								label: 'Laudos e análises enviados',
								used: data.usage.samples.used,
								max: data.usage.samples.max,
							},
						]}
					/>
					<BuyTokensCard />
					<SwitchPlanCard
						currentHierarchyRole={data.plan?.hierarchy?.role}
						onSelectPlan={(planId) => navigate(`/checkout/plano?upgrade=1&plano=${planId}`)}
					/>
					<InvoicesCard />
					{data.displayStatus !== 'canceled' && data.displayStatus !== 'none' ? (
						<CancelPlanCard
							planName={planName}
							expiresIn={data.expiresIn}
							waitCancel={data.waitCancel}
						/>
					) : null}
				</div>
				<aside className={styles.side}>
					<NextChargeCard
						rows={[
							{ label: planName, value: formatCents(chargeAmount) },
							{
								label: 'Data',
								value: data.nextCharge ? formatDate(data.nextCharge.date) : 'Sem renovação',
							},
							{ label: 'Tokens extra', value: formatCents(extraTokensAmount) },
							{ label: 'Total', value: formatCents(totalAmount), emphasis: true },
						]}
					/>
					<PaymentMethodCard
						methodLabel={getPaymentMethodLabel(data.paymentMethod)}
						documentTypeLabel={getDocumentTypeLabel(data.billing.documentType)}
						documentValue={formatDocument(data.billing.document, data.billing.documentType)}
						receiptUrl={data.billing.receiptUrl}
					/>
					<NeedMoreCard onContact={() => navigate('/suporte')} />
				</aside>
			</div>
		</div>
	);
};
